import type { MotionProps } from 'framer-motion';
import { DIMMED_OPACITY, HOVER_TRANSITION } from './gantt-constants';
import { GanttTaskStatus, type GanttTaskLayout } from './gantt-types';

const GANTT_BAR_SHOULD_RENDER_BY_STATUS: Record<GanttTaskStatus, boolean> = {
  [GanttTaskStatus.None]: true,
  [GanttTaskStatus.Done]: true,
  [GanttTaskStatus.Active]: true,
  [GanttTaskStatus.Crit]: true,
  [GanttTaskStatus.Milestone]: false,
};

const GANTT_BAR_BASE_OPACITY_BY_STATUS: Record<GanttTaskStatus, number> = {
  [GanttTaskStatus.None]: 1,
  [GanttTaskStatus.Done]: 0.6,
  [GanttTaskStatus.Active]: 1,
  [GanttTaskStatus.Crit]: 1,
  [GanttTaskStatus.Milestone]: 1,
};

const GANTT_BAR_CAN_SPLIT_BY_STATUS: Record<GanttTaskStatus, boolean> = {
  [GanttTaskStatus.None]: false,
  [GanttTaskStatus.Done]: false,
  [GanttTaskStatus.Active]: true,
  [GanttTaskStatus.Crit]: false,
  [GanttTaskStatus.Milestone]: false,
};

const MIN_SEGMENT_WIDTH = 0;
const COMPLETE_PROGRESS_RATIO = 1;
const INITIAL_GROUP_OPACITY = 0;
const INITIAL_GROUP_SCALE_X = 0;
const ANIMATED_GROUP_SCALE_X = 1;
const OPACITY_TRANSITION_DURATION = 0.28;
const SCALE_TRANSITION_DURATION = 0.4;
const ROW_STAGGER_DELAY = 0.04;
const BAR_STROKE_WIDTH = 0.5;
const REMAINING_SEGMENT_OPACITY = 0.78;
const SCALE_TRANSITION_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];

export enum GanttBarSegmentKind {
  Completed = 'completed',
  Remaining = 'remaining',
}

export interface SelectGanttBarRenderModelParams {
  taskLayout: GanttTaskLayout;
  color: string;
  gradientId: string;
  patternId: string;
  glowFilterId: string;
  isDimmed: boolean;
  isHovered: boolean;
  rowIndex: number;
}

export interface GanttBarTrackModel {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export interface GanttBarSegmentModel {
  kind: GanttBarSegmentKind;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  opacity?: number;
}

export interface GanttBarMotionModel {
  initial: MotionProps['initial'];
  animate: MotionProps['animate'];
  transition: MotionProps['transition'];
  style: MotionProps['style'];
  filter?: string;
}

export interface GanttBarRenderModel {
  track: GanttBarTrackModel;
  segments: GanttBarSegmentModel[];
  motion: GanttBarMotionModel;
}

interface SelectGanttBarSegmentsParams {
  taskLayout: GanttTaskLayout;
  gradientId: string;
  patternId: string;
}

export function selectGanttBarRenderModel({
  taskLayout,
  color,
  gradientId,
  patternId,
  glowFilterId,
  isDimmed,
  isHovered,
  rowIndex,
}: SelectGanttBarRenderModelParams): GanttBarRenderModel | null {
  if (!GANTT_BAR_SHOULD_RENDER_BY_STATUS[taskLayout.task.status]) {
    return null;
  }

  return {
    track: {
      x: taskLayout.x,
      y: taskLayout.y,
      width: taskLayout.width,
      height: taskLayout.height,
      fill: `url(#${gradientId})`,
      stroke: `${color}66`,
      strokeWidth: BAR_STROKE_WIDTH,
    },
    segments: selectGanttBarSegments({
      taskLayout,
      gradientId,
      patternId,
    }),
    motion: {
      initial: { opacity: INITIAL_GROUP_OPACITY, scaleX: INITIAL_GROUP_SCALE_X },
      animate: {
        opacity: isDimmed
          ? DIMMED_OPACITY
          : GANTT_BAR_BASE_OPACITY_BY_STATUS[taskLayout.task.status],
        scaleX: ANIMATED_GROUP_SCALE_X,
      },
      transition: {
        opacity: {
          duration: OPACITY_TRANSITION_DURATION,
          delay: rowIndex * ROW_STAGGER_DELAY,
          ease: 'easeOut',
        },
        scaleX: {
          duration: SCALE_TRANSITION_DURATION,
          delay: rowIndex * ROW_STAGGER_DELAY,
          ease: SCALE_TRANSITION_EASE,
        },
      },
      style: {
        transformOrigin: `${taskLayout.x}px ${taskLayout.centerY}px`,
        transition: HOVER_TRANSITION,
      },
      filter: isHovered ? `url(#${glowFilterId})` : undefined,
    },
  };
}

function selectGanttBarSegments({
  taskLayout,
  gradientId,
  patternId,
}: SelectGanttBarSegmentsParams): GanttBarSegmentModel[] {
  const completedWidth = Math.max(
    MIN_SEGMENT_WIDTH,
    Math.min(taskLayout.width, taskLayout.width * taskLayout.progressRatio),
  );
  const remainingWidth = Math.max(MIN_SEGMENT_WIDTH, taskLayout.width - completedWidth);
  const segments: GanttBarSegmentModel[] = [
    {
      kind: GanttBarSegmentKind.Completed,
      x: taskLayout.x,
      y: taskLayout.y,
      width: completedWidth,
      height: taskLayout.height,
      fill: `url(#${gradientId})`,
    },
    {
      kind: GanttBarSegmentKind.Remaining,
      x: taskLayout.x + completedWidth,
      y: taskLayout.y,
      width: remainingWidth,
      height: taskLayout.height,
      fill: `url(#${patternId})`,
      opacity: REMAINING_SEGMENT_OPACITY,
    },
  ];

  return shouldShowActiveSplit({ taskLayout })
    ? segments.filter((segment) => segment.width > MIN_SEGMENT_WIDTH)
    : [];
}

function shouldShowActiveSplit({
  taskLayout,
}: {
  taskLayout: GanttTaskLayout;
}): boolean {
  return GANTT_BAR_CAN_SPLIT_BY_STATUS[taskLayout.task.status]
    && taskLayout.progressRatio > MIN_SEGMENT_WIDTH
    && taskLayout.progressRatio < COMPLETE_PROGRESS_RATIO;
}
